import { useState } from 'react' // v2
import { X, Receipt, CheckSquare, Square } from 'lucide-react'
import despesasService from '../../services/despesasService'
import toast from 'react-hot-toast'

const fmt = (v) => Number(v || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })

/**
 * Modal para adicionar despesa em lotes faturados (fluxo separada).
 * Permite escolher: aplicar no primeiro lote ou em todos os lotes.
 *
 * Props:
 * - lotes: array de lotes com { id, numero_lote, vv, qtd }
 * - organizadorId: int
 * - onClose: () => void
 * - onSaved: () => void (callback após salvar para recarregar dados)
 */
export default function AdicionarDespesaModal({ lotes, organizadorId, onClose, onSaved }) {
  const [titulo, setTitulo] = useState('')
  const [valor, setValor] = useState('')
  const [modo, setModo] = useState('primeiro') // 'primeiro' | 'todos'
  const [saving, setSaving] = useState(false)

  const lotesAlvo = modo === 'primeiro' ? [lotes[0]] : lotes
  const valorNum = parseFloat(valor) || 0
  const totalImpacto = valorNum * lotesAlvo.length

  const handleSalvar = async () => {
    if (!titulo.trim()) {
      toast.error('Informe a descrição da despesa')
      return
    }
    if (valorNum <= 0) {
      toast.error('Informe um valor válido')
      return
    }

    setSaving(true)
    try {
      await despesasService.adicionarSeparadaBatch(organizadorId, {
        lote_ids: lotesAlvo.map(l => l.id),
        titulo: titulo.trim(),
        valor: valorNum,
      })
      toast.success(`Despesa adicionada em ${lotesAlvo.length} lote${lotesAlvo.length > 1 ? 's' : ''}`)
      onSaved()
      onClose()
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao adicionar despesa')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <div className="flex items-center gap-2">
            <Receipt className="w-5 h-5 text-purple-600" />
            <h2 className="text-base font-semibold text-gray-800">Adicionar Despesa</h2>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-5 space-y-4">
          {/* Descrição */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Descrição</label>
            <input
              type="text"
              value={titulo}
              onChange={e => setTitulo(e.target.value)}
              placeholder="Ex: Frete, Vacina, Curral..."
              className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              autoFocus
            />
          </div>

          {/* Valor */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Valor (R$)</label>
            <input
              type="number"
              step="0.01"
              min="0"
              value={valor}
              onChange={e => setValor(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') handleSalvar() }}
              placeholder="0,00"
              className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            />
          </div>

          {/* Modo de aplicação */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Aplicar em</label>
            <div className="space-y-2">
              <div
                onClick={() => setModo('primeiro')}
                className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-all ${
                  modo === 'primeiro' ? 'border-purple-300 bg-purple-50' : 'border-gray-200 hover:border-purple-200'
                }`}
              >
                {modo === 'primeiro' ? <CheckSquare className="w-4 h-4 text-purple-600" /> : <Square className="w-4 h-4 text-gray-400" />}
                <div>
                  <p className={`text-sm font-medium ${modo === 'primeiro' ? 'text-purple-900' : 'text-gray-700'}`}>
                    Somente no primeiro lote
                  </p>
                  <p className="text-xs text-gray-500">Lote {lotes[0]?.numero_lote} — {fmt(lotes[0]?.vv)}</p>
                </div>
              </div>
              <div
                onClick={() => setModo('todos')}
                className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-all ${
                  modo === 'todos' ? 'border-purple-300 bg-purple-50' : 'border-gray-200 hover:border-purple-200'
                }`}
              >
                {modo === 'todos' ? <CheckSquare className="w-4 h-4 text-purple-600" /> : <Square className="w-4 h-4 text-gray-400" />}
                <div>
                  <p className={`text-sm font-medium ${modo === 'todos' ? 'text-purple-900' : 'text-gray-700'}`}>
                    Em todos os lotes ({lotes.length})
                  </p>
                  <p className="text-xs text-gray-500">Uma despesa de {fmt(valorNum)} em cada lote</p>
                </div>
              </div>
            </div>
          </div>

          {/* Resumo do impacto */}
          {valorNum > 0 && (
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-600">Total de despesas a inserir:</span>
                <span className="font-bold text-gray-900">{fmt(totalImpacto)}</span>
              </div>
              <p className="text-xs text-gray-500 mt-1">
                {lotesAlvo.length} lote{lotesAlvo.length > 1 ? 's' : ''} × {fmt(valorNum)}
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex gap-3 px-6 py-4 border-t border-gray-100">
          <button onClick={onClose}
            className="flex-1 px-4 py-2.5 border border-gray-200 rounded-lg text-sm text-gray-600 hover:bg-gray-50 font-medium">
            Cancelar
          </button>
          <button onClick={handleSalvar} disabled={saving || !titulo.trim() || valorNum <= 0}
            className="flex-1 px-4 py-2.5 bg-purple-600 hover:bg-purple-700 text-white rounded-lg text-sm font-medium disabled:opacity-50 transition-colors">
            {saving ? 'Salvando...' : `Adicionar em ${lotesAlvo.length} lote${lotesAlvo.length > 1 ? 's' : ''}`}
          </button>
        </div>
      </div>
    </div>
  )
}
